/**
 * Select Component
 * Dropdown selects with ARIA support
 */
import React, { useMemo } from 'react';
import { ChevronDown } from 'lucide-react';

export interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  error?: string;
  helperText?: string;
  options: Array<{ value: string; label: string }>;
}

export const Select: React.FC<SelectProps> = ({
  label,
  error,
  helperText,
  options,
  className = '',
  id,
  ...props
}) => {
  // Generate unique IDs for accessibility
  const generatedId = React.useId();
  const fieldId = useMemo(() => id || `select-${generatedId.replaceAll(':', '-')}`, [id, generatedId]);
  const errorId = `${fieldId}-error`;
  const helpId = `${fieldId}-help`;
  const labelId = label ? `${fieldId}-label` : undefined;

  const selectClasses = `
    w-full border rounded-lg text-sm bg-white dark:bg-slate-800 text-slate-900 dark:text-white
    focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-colors appearance-none
    ${error ? 'border-red-500 focus:ring-red-500' : 'border-slate-300 dark:border-slate-600'}
    pl-3 pr-10 py-2.5
    ${className}
  `;

  // Build aria-describedby attribute
  const ariaDescribedBy = useMemo(() => {
    const ids: string[] = [];
    if (error) ids.push(errorId);
    else if (helperText) ids.push(helpId);
    return ids.length > 0 ? ids.join(' ') : undefined;
  }, [error, helperText, errorId, helpId]);

  return (
    <div className="w-full">
      {label && (
        <label 
          id={labelId}
          htmlFor={fieldId}
          className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase mb-1"
        >
          {label}
        </label>
      )}
      <div className="relative">
        <select
          id={fieldId}
          className={selectClasses}
          aria-invalid={error ? 'true' : undefined}
          aria-labelledby={labelId}
          aria-describedby={ariaDescribedBy}
          {...props}
        >
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <ChevronDown
          size={16}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
          aria-hidden="true"
        />
      </div>
      {error && (
        <p 
          id={errorId}
          className="mt-1 text-xs text-red-600 dark:text-red-400"
          role="alert"
        >
          {error}
        </p>
      )}
      {helperText && !error && (
        <p 
          id={helpId}
          className="mt-1 text-xs text-slate-500 dark:text-slate-400"
        >
          {helperText}
        </p>
      )}
    </div>
  );
};

